/**
 * 사업계획서 HWP 누름틀 값 반영/추출 (@rhwp/core)
 * — 에디터(@rhwp/editor)에 Field API가 없어 export → core 로 우회
 */

let coreReady = null;

function ensureMeasureTextWidth() {
  const g = typeof window !== 'undefined' ? window : global;
  if (typeof g.measureTextWidth === 'function') return;
  let ctx = null;
  let lastFont = '';
  g.measureTextWidth = (font, text) => {
    if (typeof document === 'undefined') {
      return String(text || '').length * 8;
    }
    if (!ctx) ctx = document.createElement('canvas').getContext('2d');
    if (font !== lastFont) {
      ctx.font = font;
      lastFont = font;
    }
    return ctx.measureText(String(text ?? '')).width;
  };
}

async function ensureCore() {
  if (coreReady) return coreReady;
  coreReady = (async () => {
    ensureMeasureTextWidth();
    const mod = await import('@rhwp/core');
    const init = mod.default;
    await init({ module_or_path: '/rhwp_bg.wasm' });
    return mod;
  })();
  return coreReady;
}

function parseMaybeJson(v) {
  if (v == null) return v;
  if (typeof v === 'object') return v;
  try {
    return JSON.parse(v);
  } catch {
    return { raw: v };
  }
}

/** 가이드문·빈값 정리 */
export function normalizeFieldValue(value, guide = '') {
  let text = value == null ? '' : String(value).trim();
  if (!text) return '';
  const g = String(guide || '').trim();
  if (g && (text === g || text === `○ ${g}` || text.startsWith(`${g} :`) || text.startsWith(`${g}:`))) {
    return '';
  }
  // 흔한 안내 문구
  const guides = [
    '부서명',
    '사업명',
    '사업위치',
    '사업기간',
    '총사업비(천원)',
    '사업내용',
    '편성목',
    '예산액',
    '산출내역',
  ];
  if (guides.includes(text)) return '';
  return text;
}

function valueFromGetByName(raw) {
  const detail = parseMaybeJson(raw);
  if (detail?.value != null) return String(detail.value);
  if (typeof raw === 'string' && !raw.trim().startsWith('{')) return raw;
  if (detail?.raw != null) return String(detail.raw);
  return '';
}

/**
 * HWP 바이트에 누름틀(name) 값을 일괄 반영 후 새 바이트 반환
 */
export async function applyFieldValuesToHwp(hwpBytes, fieldValues) {
  const { HwpDocument } = await ensureCore();
  const input =
    hwpBytes instanceof Uint8Array ? hwpBytes : new Uint8Array(hwpBytes);
  const doc = new HwpDocument(input);
  const results = [];
  try {
    Object.entries(fieldValues || {}).forEach(([name, value]) => {
      if (!name) return;
      const text = value == null ? '' : String(value);
      try {
        const raw = doc.setFieldValueByName(name, text);
        const detail = parseMaybeJson(raw);
        results.push({
          name,
          ok: detail?.ok !== false,
          detail,
        });
      } catch (e) {
        results.push({ name, ok: false, detail: String(e?.message || e) });
      }
    });
    const bytes = doc.exportHwp();
    return { bytes, results };
  } finally {
    try {
      doc.free?.();
    } catch (_) {
      /* ignore */
    }
  }
}

/**
 * HWP에서 누름틀 값 추출
 * 1) getFieldList().value 우선  2) getFieldValueByName 보완  3) fieldId getFieldValue
 */
export async function extractFieldValuesFromHwp(hwpBytes, fieldNames = []) {
  const { HwpDocument } = await ensureCore();
  const input =
    hwpBytes instanceof Uint8Array ? hwpBytes : new Uint8Array(hwpBytes);
  const doc = new HwpDocument(input);
  const fields = {};
  const meta = {};
  try {
    let list = [];
    try {
      const parsed = parseMaybeJson(doc.getFieldList());
      list = Array.isArray(parsed) ? parsed : [];
    } catch {
      list = [];
    }

    list.forEach((f) => {
      const name = f?.name;
      if (!name) return;
      meta[name] = { guide: f.guide || '', fieldId: f.fieldId };
      fields[name] = normalizeFieldValue(f.value, f.guide);
    });

    const names =
      fieldNames.length > 0
        ? fieldNames
        : list.map((f) => f?.name).filter(Boolean);

    names.forEach((name) => {
      const guide = meta[name]?.guide || '';
      if (fields[name]) return;
      try {
        const byName = normalizeFieldValue(
          valueFromGetByName(doc.getFieldValueByName(name)),
          guide,
        );
        if (byName) {
          fields[name] = byName;
          return;
        }
      } catch {
        /* continue */
      }
      const fid = meta[name]?.fieldId;
      if (fid != null) {
        try {
          const byId = normalizeFieldValue(
            valueFromGetByName(doc.getFieldValue(Number(fid))),
            guide,
          );
          if (byId) fields[name] = byId;
        } catch {
          /* ignore */
        }
      }
      if (fields[name] == null) fields[name] = '';
    });

    return fields;
  } finally {
    try {
      doc.free?.();
    } catch (_) {
      /* ignore */
    }
  }
}

/** 필드 맵 병합 — 뒤에 온 비어 있지 않은 값이 우선 */
export function mergeFieldMaps(...maps) {
  const out = {};
  maps.forEach((m) => {
    Object.entries(m || {}).forEach(([k, v]) => {
      const text = normalizeFieldValue(v);
      if (!text) return;
      out[k] = text;
    });
  });
  return out;
}

/** 필드맵 input 타입 → 조회 팝업 종류 */
export function lookupKindForInput(input) {
  if (input === 'lookupDept' || input === 'sessionDept' || input === 'popupDept') {
    return 'dept';
  }
  if (input === 'popupProject' || input === 'lookupProject') {
    return 'project';
  }
  return null;
}
